/**
 * Step 2 — Primary goal picker — SYNTHESIS variant
 *
 * Spec: specs/onboarding-rebuild.md → Act 1 step 2
 *
 * Synthesizes:
 *   - Professional's OptionRow architecture as the spine (hover + selected
 *     state machine, refined leading + spacing, trailing check that morphs
 *     in 40ms AFTER the row settles).
 *   - Professional's Continue wake-up (visually inert when disabled, 320ms
 *     ease-out-expo to gold + inner highlight sheen on enable).
 *   - Experimental's gold-ripple glint that sweeps across the just-selected
 *     row (520ms left-to-right). Pure delight beat; respects reduced motion.
 *
 * What synthesis explicitly drops:
 *   - Experimental's heading mutation (over-engineered).
 *   - Confirmation line ("This is what brought you here.") beneath the list —
 *     Mason's call, the selection itself is the acknowledgement.
 *   - "Step 2 of 18" eyebrow (banned per Mason; humanizer log).
 *   - "One last thing" / "A moment to name it" eyebrows.
 */

import { useEffect, useRef, useState } from 'react'
import ContinueButton from '@/components/onboarding/ContinueButton'

const EASE_OUT_EXPO = 'cubic-bezier(0.16, 1, 0.3, 1)'

export default function PrimaryGoalSynthesis({ value, onSelect, onContinue, options }) {
  const hasSelection = !!value
  const [hoverIndex, setHoverIndex] = useState(null)
  // continueHover state retired 2026-05-17 — CTA migrated to shared
  // ContinueButton which owns its own hover treatment.

  // Gold-ripple glint state. When value changes, set ripplingValue to the new
  // value, then clear it ~520ms later (matches the keyframe duration). Driven
  // entirely by state so the render stays pure — no performance.now() in render.
  const [ripplingValue, setRipplingValue] = useState(null)
  const rippleValueRef = useRef(value)

  useEffect(() => {
    if (value && value !== rippleValueRef.current) {
      rippleValueRef.current = value
      // Respect reduced-motion at the moment the ripple would fire.
      const reduced =
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (reduced) return undefined
      setRipplingValue(value)
      const t = setTimeout(() => setRipplingValue(null), 520)
      return () => clearTimeout(t)
    }
    return undefined
  }, [value])

  return (
    <div className="w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <style>{`
        @keyframes synGoalRipple {
          0%   { transform: translateX(-100%); opacity: 0; }
          25%  { opacity: 1; }
          100% { transform: translateX(100%); opacity: 0; }
        }
      `}</style>

      {/* Heading — serif, gentle authority */}
      <div className="text-center mb-7 sm:mb-9">
        <h1
          className="font-heading text-[26px] sm:text-[32px] font-extrabold text-[var(--text-primary)]"
          style={{ lineHeight: 1.15, letterSpacing: '-0.012em' }}
        >
          What brings you here?
        </h1>
      </div>

      {/* Options — Professional's row architecture + experimental's ripple */}
      <div
        role="radiogroup"
        aria-label="What brings you to Illuminate Faith?"
        className="flex flex-col gap-2.5"
      >
        {options.map((opt, i) => {
          const selected = value === opt.value
          const hovered = hoverIndex === i && !selected
          const rippling = selected && ripplingValue === opt.value

          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onSelect(opt.value)}
              onMouseEnter={() => setHoverIndex(i)}
              onMouseLeave={() => setHoverIndex(null)}
              onFocus={() => setHoverIndex(i)}
              onBlur={() => setHoverIndex(null)}
              className="group relative w-full text-left rounded-xl overflow-hidden focus:outline-none"
              style={{
                background: selected
                  ? 'var(--gold-select)'
                  : hovered
                    ? 'var(--bg-surface-high)'
                    : 'var(--bg-surface)',
                border: selected
                  ? '1.5px solid var(--gold)'
                  : hovered
                    ? '1px solid var(--gold-dim)'
                    : '1px solid var(--glass-border)',
                // 1.5px borders shift inner box by 0.5px vs 1px borders —
                // counter that so the row's content baseline never jitters.
                padding: selected ? '13.5px 17.5px' : '14px 18px',
                transition: `background 240ms ${EASE_OUT_EXPO}, border-color 240ms ${EASE_OUT_EXPO}, transform 180ms ${EASE_OUT_EXPO}`,
                minHeight: '56px',
              }}
            >
              {/* Gold ripple glint — sweeps across the row on freshly-selected.
                  Driven by ripplingValue state; only the active row renders this. */}
              {rippling && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent 0%, rgba(233, 195, 73, 0.22) 50%, transparent 100%)',
                    animation: 'synGoalRipple 520ms ease-out forwards',
                  }}
                />
              )}

              <div className="relative flex items-center gap-3">
                <span
                  className="flex-1 text-[14.5px] sm:text-[15px] font-medium text-[var(--text-primary)]"
                  style={{
                    lineHeight: 1.45,
                    letterSpacing: '-0.005em',
                    transform: hovered ? 'translateX(2px)' : 'translateX(0)',
                    transition: `transform 180ms ${EASE_OUT_EXPO}`,
                  }}
                >
                  {opt.label}
                </span>

                {/* Trailing check — 40ms-delayed morph-in after row settles */}
                <span
                  aria-hidden="true"
                  className="flex-shrink-0 flex items-center justify-center"
                  style={{
                    width: '18px',
                    height: '18px',
                    opacity: selected ? 1 : 0,
                    transform: selected ? 'scale(1) translateY(0)' : 'scale(0.6) translateY(2px)',
                    transition: `opacity 280ms ${EASE_OUT_EXPO} 40ms, transform 280ms ${EASE_OUT_EXPO} 40ms`,
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="9" cy="9" r="9" fill="var(--gold)" />
                    <path
                      d="M5 9.2 7.6 11.7 13 6.3"
                      stroke="var(--text-on-gold)"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </button>
          )
        })}
      </div>

      {/* Continue — migrated to shared ContinueButton 2026-05-17 for
          funnel-wide consistency. Disabled state inherits from the shared
          component (surface-high bg, muted text, not-allowed cursor).
          flex-col so the "Pick one to continue" hint sits BELOW the button,
          not awkwardly beside it on desktop (Mason 2026-05-17). */}
      <div className="mt-7 sm:mt-9 flex flex-col items-center">
        <ContinueButton onClick={onContinue} disabled={!hasSelection}>
          Continue
        </ContinueButton>
        <p
          aria-hidden={hasSelection}
          className="text-center text-[12px] text-[var(--text-muted-soft)] mt-3"
          style={{
            opacity: hasSelection ? 0 : 1,
            transition: `opacity 240ms ${EASE_OUT_EXPO}`,
            minHeight: '1em',
          }}
        >
          Pick one to continue.
        </p>
      </div>
    </div>
  )
}
